import { Audience } from '@fluidx/office-fluid-types';
import { safeLocalStorage } from './localStorage';

const settingsCache = new Map<string, boolean>();

// Task 4484308: Clean up old Audience Values
const audienceMapping = new Map<Audience, number>([
  ['Test', 0],
  ['Automation', 0],
  ['Local', 1],
  ['Staging', 2],
  ['Dogfood', 3],
  ['MSIT', 4],
  ['Microsoft', 4],
  ['SyntheticTraffic', 4],
  ['Production', 5]
]);

interface settingsConfig {
  name: string;
  defaultValue: boolean;
}

/**
 * Obtains the value of the config.name setting with the following priority for overrides:
 * Query Param > localStorage > default
 */
export function getSettingsValue(config: settingsConfig): boolean {
  const { name, defaultValue } = config;

  const settingValue = getAndCacheSettingValue(name, defaultValue);

  return settingValue;
}

/*
 * This method should not be used outside of tests. It is intended to allow testing
 * of features off and on.
 */
export function clearSettingsCache(name: string): void {
  settingsCache.delete(name);
}

function getAndCacheSettingValue(name: string, defaultValue: boolean, audienceValue?: boolean): boolean {
  // Cache used to store the value of the setting within a session.
  // A page reload should be required after setting a value in localStorage.
  const cacheValue = settingsCache.get(name);
  if (cacheValue !== undefined) return cacheValue;

  let queryParamValue: boolean | undefined;
  let localStorageValue: boolean | undefined;
  let settingValue: boolean;

  if ((queryParamValue = getFromQueryParam(name)) !== undefined) {
    settingValue = queryParamValue;
  } else if ((localStorageValue = getFromLocalStorage(name)) !== undefined) {
    settingValue = localStorageValue;
  } else if (audienceValue !== undefined) {
    settingValue = audienceValue;
  } else {
    settingValue = defaultValue;
  }

  settingsCache.set(name, settingValue);
  return settingValue;
}
/**
 * Stores override for given setting in local storage. Note that this will have no impact on current session
 * if this function was called after getSettingsValue(): returned settings value presumed immutable for the
 * entire session lifetime.
 * @param name Settings name to override
 * @param overrideValue Settings value to be stored
 */
export function storeSettingsValueOverride(name: string, overrideValue: boolean) {
  safeLocalStorage()?.setItem(name, overrideValue ? 'on' : 'off');
}

/**
 * Clears override for given setting from local storage. Note that this will have no impact on current session
 * if this function was called after getSettingsValue(): returned settings value presumed immutable for the
 * entire session lifetime.
 * @param name Settings name which override will be cleared
 */
export function clearSettingsValueOverride(name: string) {
  safeLocalStorage()?.removeItem(name);
}

/**
 * Helper function to enable features up to a specific environment.
 * 'Test' === 'Automation' < 'Local' < 'Staging' < 'Dogfood' < 'MSIT' === 'Microsoft'  === 'SyntheticTraffic' < 'Production'
 * Task 4484308: Clean up old Audience Values
 * @returns true if the Audience value represents an audience below the one stated, false otherwise. If audience is undefined, we assume production
 */
export function enableUpToAudience(enableUpTo: Audience, audience: Audience | undefined): boolean {
  let audienceToUse = audience;
  if (audienceToUse === undefined) {
    audienceToUse = 'Production'; // If audience is undefined, we assume production to be safe.
  }

  const enableUpToValue = audienceMapping.get(enableUpTo);
  const audienceValue = audienceMapping.get(audienceToUse);

  return enableUpToValue !== undefined && audienceValue !== undefined && audienceValue <= enableUpToValue;
}

const getFromLocalStorage = (keyName: string): boolean | undefined => {
  const localStorageValue = safeLocalStorage()?.getItem(keyName);
  return localStorageValue === 'on' ? true : localStorageValue === 'off' ? false : undefined;
};

const getFromQueryParam = (keyName: string): boolean | undefined => {
  const queryParams = new URLSearchParams(location.search);
  const paramValue = queryParams.get(keyName);

  return paramValue === 'on' ? true : paramValue === 'off' ? false : undefined;
};
